import {
  CouponInfoSheet,
  HighTierCouponsSection as HighTierCouponsSectionType,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {mobileOnly, tabletAndUp} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';
import {getCouponPromotionId} from '../../getCouponPromotionId';
import {CouponCardWithLink} from './CouponCardWithLink';
import {CouponStreamHeadline} from './CouponStreamHeadline';
import {HighTierSlider} from './HighTierSlider';

export interface HighTierCouponsSectionProps
  extends HighTierCouponsSectionType {
  handleInfoIconClick: (couponInfo?: CouponInfoSheet) => void;
  stateManagerCustomKey: string;
  setSelectedProduct?: (product?: Product) => void;
  onCloseInfoSheet: React.MutableRefObject<VoidFunction | undefined>;
  darkMode?: boolean;
  paused?: boolean;
  makeInert: boolean;
}

export const HighTierCouponsSection: FC<HighTierCouponsSectionProps> = ({
  coupons,
  headline,
  handleInfoIconClick,
  stateManagerCustomKey,
  setSelectedProduct,
  onCloseInfoSheet,
  darkMode,
  paused,
  makeInert,
}) => {
  return (
    <Wrapper inert={makeInert} data-testid="highTierCouponsSection">
      <StyledCouponStreamHeadline
        count={coupons.length}
        headline={headline}
        darkMode={darkMode}
      />
      <CouponsSectionWrapper data-paused={paused}>
        <CouponsSection
          coupons={coupons}
          handleInfoIconClick={handleInfoIconClick}
          stateManagerCustomKey={stateManagerCustomKey}
          setSelectedProduct={setSelectedProduct}
          onCloseInfoSheet={onCloseInfoSheet}
          darkMode={darkMode}
          paused={paused}
        />
      </CouponsSectionWrapper>
    </Wrapper>
  );
};

const CouponsSection: FC<
  Omit<HighTierCouponsSectionProps, 'headline' | 'makeInert'>
> = ({
  coupons,
  handleInfoIconClick,
  stateManagerCustomKey,
  setSelectedProduct,
  onCloseInfoSheet,
  darkMode,
  paused,
}) => {
  const renderHighTierCouponsSlider =
    coupons.length > 1 && process.env.STACK === 'mobile';

  if (renderHighTierCouponsSlider) {
    return (
      <HighTierSlider
        coupons={coupons}
        handleInfoIconClick={handleInfoIconClick}
        setSelectedProduct={setSelectedProduct}
        stateManagerCustomKey={stateManagerCustomKey}
        onCloseInfoSheet={onCloseInfoSheet}
        darkMode={darkMode}
        paused={paused}
      />
    );
  }

  return (
    <HighTierWrapper $addSidePadding={coupons.length === 1}>
      {coupons.map(coupon => (
        <CouponCardWithLink
          key={getCouponPromotionId(coupon.coupon)}
          coupon={coupon}
          handleInfoIconClick={handleInfoIconClick}
          setSelectedProduct={setSelectedProduct}
          stateManagerCustomKey={stateManagerCustomKey}
        />
      ))}
    </HighTierWrapper>
  );
};

const StyledCouponStreamHeadline = styled(CouponStreamHeadline)`
  padding-inline: 16px;
`;

const Wrapper = styled.div`
  padding-block: 12px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  // TODO: Remove once coins is live https://aboutyou.atlassian.net/browse/ASFE-11878
  [data-paused-section='true'] & {
    opacity: 0.3;
    pointer-events: none;
    cursor: none;
  }
`;

const CouponsSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  &[data-paused='true'] {
    opacity: 0.6;
    pointer-events: none;
    cursor: none;
  }
`;

const HighTierWrapper = styled.div<{$addSidePadding?: boolean}>`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  gap: 10px;

  ${({$addSidePadding}) =>
    $addSidePadding &&
    mobileOnly(css`
      padding-inline: 16px;
    `)}

  ${tabletAndUp(css`
    padding-inline: 16px;
  `)}
`;
